import { useMemo, useState } from "react";
import { PageHeader } from "@/components/data-display/page-header";
import { StatCard } from "@/components/data-display/stat-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/feedback";
import { IdentifierValue } from "@/components/ui/identifier-value";
import { Pagination } from "@/components/ui/pagination";
import { Table, TableWrapper } from "@/components/ui/table";
import { LoadingTable } from "@/components/ui/system-state";
import { ProviderMigrationNav } from "@/features/provider-migration/journey-nav";
import { buildSnapshotVaultNames } from "@/features/provider-migration/snapshot-vault-names";
import {
  CATEGORY_LABEL,
  inventoryAttribute,
  ReadOnlyBanner,
  snapshotFreshness,
  UnavailableWithRetry,
} from "@/features/provider-migration/shared";
import { useProviderMigrationInventory } from "@/hooks/use-provider-migration";
import type { ApiProviderMigrationInventoryItem, ProviderMigrationCategory } from "@/lib/api";

const PAGE_SIZE = 25;
const NOT_AVAILABLE = "—";

/** Attribute keys searched alongside the provider resource id. */
const SEARCH_KEYS = ["name", "vaultAccountId", "assetId", "address"];

function matches(item: ApiProviderMigrationInventoryItem, q: string): boolean {
  if (item.providerResourceId.toLowerCase().includes(q)) return true;
  return SEARCH_KEYS.some((key) => inventoryAttribute(item, key)?.toLowerCase().includes(q));
}

/** Asset wallets and deposit addresses are grouped by vault, then asset; ids compare numerically. */
function byVaultThenAsset(a: ApiProviderMigrationInventoryItem, b: ApiProviderMigrationInventoryItem): number {
  const vault = (inventoryAttribute(a, "vaultAccountId") ?? "").localeCompare(inventoryAttribute(b, "vaultAccountId") ?? "", undefined, { numeric: true });
  if (vault !== 0) return vault;
  return (inventoryAttribute(a, "assetId") ?? "").localeCompare(inventoryAttribute(b, "assetId") ?? "");
}

function Text({ value }: { value: string | null }) {
  return value === null ? <span className="text-subtle" title="Not available">{NOT_AVAILABLE}</span> : <>{value}</>;
}

/** An exact decimal string as the provider sent it, never parsed to a number. */
function Amount({ value }: { value: string | null }) {
  return value === null ? <Text value={null} /> : <span className="font-mono text-xs tabular-nums">{value}</span>;
}

function VaultCell({ item, vaultNames }: { item: ApiProviderMigrationInventoryItem; vaultNames: Map<string, string> }) {
  const vaultId = inventoryAttribute(item, "vaultAccountId");
  if (vaultId === null) return <Text value={null} />;
  const name = vaultNames.get(vaultId);
  return (
    <span className="flex flex-col">
      {name && <span className="text-ink">{name}</span>}
      <IdentifierValue value={vaultId} />
    </span>
  );
}

function InventoryTable({
  category,
  items,
  vaultNames,
}: {
  category: ProviderMigrationCategory;
  items: ApiProviderMigrationInventoryItem[];
  vaultNames: Map<string, string>;
}) {
  if (category === "VAULT_ACCOUNTS") {
    return (
      <Table>
        <thead>
          <tr>
            <th>Vault name</th>
            <th>Vault id</th>
            <th>Hidden in provider UI</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const hidden = inventoryAttribute(item, "hiddenOnUi");
            return (
              <tr key={`${item.resourceType}:${item.providerResourceId}`}>
                <td><Text value={inventoryAttribute(item, "name")} /></td>
                <td><IdentifierValue value={item.providerResourceId} /></td>
                <td>{hidden === "true" ? "Yes" : hidden === "false" ? "No" : <Text value={null} />}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  }
  if (category === "ASSET_WALLETS") {
    return (
      <Table>
        <thead>
          <tr>
            <th>Vault</th>
            <th>Asset</th>
            <th>Total</th>
            <th>Available</th>
            <th>Provider resource id</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={`${item.resourceType}:${item.providerResourceId}`}>
              <td><VaultCell item={item} vaultNames={vaultNames} /></td>
              <td><Text value={inventoryAttribute(item, "assetId")} /></td>
              <td><Amount value={inventoryAttribute(item, "total")} /></td>
              <td><Amount value={inventoryAttribute(item, "available")} /></td>
              <td><IdentifierValue value={item.providerResourceId} /></td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }
  return (
    <Table>
      <thead>
        <tr>
          <th>Vault</th>
          <th>Asset</th>
          <th>Deposit address</th>
          <th>Format</th>
          <th>Type</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => {
          const address = inventoryAttribute(item, "address");
          return (
            <tr key={`${item.resourceType}:${item.providerResourceId}`}>
              <td><VaultCell item={item} vaultNames={vaultNames} /></td>
              <td><Text value={inventoryAttribute(item, "assetId")} /></td>
              <td>{address === null ? <Text value={null} /> : <IdentifierValue value={address} />}</td>
              <td><Text value={inventoryAttribute(item, "addressFormat")} /></td>
              <td><Text value={inventoryAttribute(item, "type")} /></td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}
const CATEGORIES: ProviderMigrationCategory[] = ["VAULT_ACCOUNTS", "ASSET_WALLETS", "VAULT_ADDRESSES"];

/** Provider migration, step 2: discovery (page spec provider-migration-discovery). */
export default function ProviderMigrationDiscoveryPage() {
  const vaults = useProviderMigrationInventory("VAULT_ACCOUNTS");
  const wallets = useProviderMigrationInventory("ASSET_WALLETS");
  const addresses = useProviderMigrationInventory("VAULT_ADDRESSES");
  const byCategory = { VAULT_ACCOUNTS: vaults, ASSET_WALLETS: wallets, VAULT_ADDRESSES: addresses };

  const [category, setCategory] = useState<ProviderMigrationCategory>("VAULT_ACCOUNTS");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const active = byCategory[category];

  const vaultNames = useMemo(
    () => buildSnapshotVaultNames(vaults.data, active.data?.snapshotId, !vaults.loading && !vaults.error),
    [vaults.data, active.data?.snapshotId, vaults.loading, vaults.error],
  );

  const rows = useMemo(() => {
    const items = active.data?.items ?? [];
    const q = query.trim().toLowerCase();
    const filtered = q ? items.filter((i) => matches(i, q)) : items;
    return category === "VAULT_ACCOUNTS" ? filtered : [...filtered].sort(byVaultThenAsset);
  }, [active.data, query, category]);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const visible = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const freshness = snapshotFreshness(active.data?.freshUntil ?? null);

  const refreshAll = () => {
    vaults.refresh();
    wallets.refresh();
    addresses.refresh();
  };
  const count = (c: ProviderMigrationCategory) => {
    const r = byCategory[c];
    return r.loading ? "…" : r.error || !r.data ? "—" : `${r.data.items.length}${r.data.truncated ? "+" : ""}`;
  };
  const clearFilters = () => {
    setQuery("");
    setPage(1);
  };

  return (
    <div>
      <PageHeader
        title="Discovery"
        subtitle="Vault accounts, asset wallets and deposit addresses found in the latest sealed provider snapshot."
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={refreshAll}>
              Refresh
            </Button>
          </>
        }
      />
      <ProviderMigrationNav active="discovery" />
      <ReadOnlyBanner />

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            aria-pressed={category === c}
            onClick={() => {
              setCategory(c);
              setPage(1);
            }}
            className={`rounded-lg text-left ${category === c ? "ring-2 ring-primary" : ""}`}
          >
            <StatCard value={count(c)} label={CATEGORY_LABEL[c]} />
          </button>
        ))}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
          placeholder="Search vault, asset, address or id"
          aria-label="Search provider inventory"
          className="w-full max-w-md rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-subtle focus:outline-none focus:ring-2 focus:ring-primary-soft"
        />
        {query && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear
          </Button>
        )}
        {active.data?.snapshotId && (
          <span className="flex flex-wrap items-center gap-2 text-xs text-muted">
            Snapshot <IdentifierValue value={active.data.snapshotId} />
            <Badge variant={freshness === "fresh" ? "success" : freshness === "stale" ? "warning" : "neutral"}>
              {freshness === "fresh" ? "Fresh" : freshness === "stale" ? "Stale" : "Freshness unknown"}
            </Badge>
          </span>
        )}
      </div>

      {active.loading && <LoadingTable rows={5} cols={5} />}
      {!active.loading && active.error && (
        <UnavailableWithRetry title={`${CATEGORY_LABEL[category]} are not available`} error={active.error} onRetry={active.refresh} />
      )}
      {!active.loading && !active.error && active.data && active.data.items.length === 0 && (
        <EmptyState card title={`No ${CATEGORY_LABEL[category].toLowerCase()} in this snapshot`} description="The provider snapshot contains none of this resource type." />
      )}
      {!active.loading && !active.error && active.data && active.data.items.length > 0 && rows.length === 0 && (
        <EmptyState card title="No results" description="No vault, asset, address or id matches this search." action={<Button variant="secondary" size="sm" onClick={clearFilters}>Clear search</Button>} />
      )}
      {!active.loading && !active.error && rows.length > 0 && (
        <TableWrapper>
          <InventoryTable category={category} items={visible} vaultNames={vaultNames} />
          <Pagination page={safePage} pageCount={pageCount} onPageChange={setPage} totalItems={rows.length} pageSize={PAGE_SIZE} />
        </TableWrapper>
      )}
      {active.data?.truncated && (
        <p className="mt-3 text-xs text-warning">
          Showing the first {active.data.items.length} items. The inventory is larger than this view reads, so this is not the complete list.
        </p>
      )}
    </div>
  );
}
